import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook to manage data refresh on a regular interval
 * Aligns refresh to specific minute intervals and handles page visibility
 *
 * @param fetchDataFn - Function to call for fetching data
 * @param intervalMinutes - Refresh interval in minutes (default: 5)
 * @param customNow - Optional custom time; disables all auto-refresh when set
 */
export function useDataRefresh(
  fetchDataFn: () => Promise<void>,
  intervalMinutes: number = 5,
  customNow?: Date | null
) {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const refreshIntervalIdRef = useRef<number | null>(null);
  const wasHiddenRef = useRef(false);

  // Calculate time until next aligned refresh time
  const calculateNextRefreshTime = useCallback(() => {
    const now = new Date();
    const minutes = now.getMinutes();
    const nextMinutes = Math.ceil(minutes / intervalMinutes) * intervalMinutes;
    
    const nextRefresh = new Date(now);
    nextRefresh.setMinutes(nextMinutes, 0, 0);
    
    // If we're already past the calculated time, add interval
    if (nextRefresh <= now) {
      nextRefresh.setMinutes(nextRefresh.getMinutes() + intervalMinutes);
    }
    
    return nextRefresh.getTime() - now.getTime();
  }, [intervalMinutes]);

  // Setup the refresh interval
  const setupRefreshInterval = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalIdRef.current) {
      clearTimeout(refreshIntervalIdRef.current);
    }

    const timeToNext = calculateNextRefreshTime();
    
    refreshIntervalIdRef.current = window.setTimeout(() => {
      fetchDataFn();
      setLastUpdated(new Date());
      setupRefreshInterval(); // Schedule next refresh
    }, timeToNext);
  }, [calculateNextRefreshTime, fetchDataFn]);

  // Handle page visibility changes
  useEffect(() => {
    // Skip visibility handling when using custom time
    if (customNow) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, stop refresh
        wasHiddenRef.current = true;
        if (refreshIntervalIdRef.current) {
          clearTimeout(refreshIntervalIdRef.current);
          refreshIntervalIdRef.current = null;
        }
      } else if (wasHiddenRef.current) {
        // Page is visible again after being hidden
        wasHiddenRef.current = false;
        // Refresh immediately and restart interval
        fetchDataFn();
        setLastUpdated(new Date());
        setupRefreshInterval();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchDataFn, setupRefreshInterval, customNow]);

  // Handle online/offline status
  useEffect(() => {
    // Skip online/offline handling when using custom time
    if (customNow) return;

    const handleOnline = () => {
      // When coming back online, refresh data
      fetchDataFn();
      setLastUpdated(new Date());
      setupRefreshInterval();
    };

    const handleOffline = () => {
      // When going offline, stop refresh
      if (refreshIntervalIdRef.current) {
        clearTimeout(refreshIntervalIdRef.current);
        refreshIntervalIdRef.current = null;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchDataFn, setupRefreshInterval, customNow]);

  // Setup initial interval
  useEffect(() => {
    // Skip interval setup when using custom time
    if (customNow) return;

    setupRefreshInterval();

    return () => {
      if (refreshIntervalIdRef.current) {
        clearTimeout(refreshIntervalIdRef.current);
      }
    };
  }, [setupRefreshInterval, customNow]);

  return { lastUpdated };
}